import { fetchWithAuth } from './api';

const REST_BASE = (import.meta.env.VITE_REST_API_URL ?? '').replace(/\/$/, '');

// ── Types ──────────────────────────────────────────────────────────────────────

export interface StoryTopic {
  id: string;
  areaId: string;
  title: string;
  status: 'pending' | 'captured' | 'complete';
  order: number;
  sessionIds: string[];
  createdAt: string;
}

export interface StoryTopicsResponse {
  topics: StoryTopic[];
}

// ── API functions ──────────────────────────────────────────────────────────────

/**
 * Fetch all story topics for the current user.
 */
export async function fetchStoryTopics(): Promise<StoryTopic[]> {
  const response = await fetchWithAuth(`${REST_BASE}/story-topics`);
  if (!response.ok) {
    throw new Error(`GET /story-topics returned ${response.status}`);
  }
  const data = (await response.json()) as StoryTopicsResponse;
  return data.topics;
}

/**
 * Replace the full story topics list for the current user.
 */
export async function saveStoryTopics(topics: StoryTopic[]): Promise<void> {
  const response = await fetchWithAuth(`${REST_BASE}/story-topics`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ topics }),
  });
  if (!response.ok) {
    throw new Error(`PUT /story-topics returned ${response.status}`);
  }
}

/**
 * Partially update a single story topic (e.g. status or sessionIds).
 */
export async function patchStoryTopic(
  topicId: string,
  update: { status?: StoryTopic['status']; sessionIds?: string[] },
): Promise<void> {
  const response = await fetchWithAuth(`${REST_BASE}/story-topics/${topicId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  });
  if (!response.ok) {
    throw new Error(`PATCH /story-topics/${topicId} returned ${response.status}`);
  }
}
